/**
 * Finding the object's real edge from a rough shape drawn round it — on the device.
 *
 * Smart Crop's cleverness came entirely from a cut-out service. When that service is out of
 * quota, unreachable, or simply not configured, Smart Crop had nothing left but the shape the
 * person drew, which makes it identical to Freeform Crop — the complaint it exists to answer.
 *
 * This is the classic method for exactly that situation, and it needs no network, no model
 * download and no allowance: you draw roughly round a thing; the algorithm learns what colours
 * are inside and what colours are outside, then finds the boundary that best separates them
 * while preferring to run along edges in the picture rather than through flat areas.
 *
 * Why it is not the colour flood fill that was already here. That one grows a region from one
 * pixel while the colour stays similar, so a face against a wall of the same tone is one region
 * and it returns the face with black wedges bitten out of it. This asks a different question —
 * not "what is connected and similar to this pixel" but "where should the line go, given
 * everything inside and everything outside" — and answers it globally, all pixels at once, by a
 * minimum cut. That global answer is why the boundary comes out connected and smooth instead of
 * in slabs.
 *
 * ── How it works ──────────────────────────────────────────────────────────────────────────
 *  1. The drawn shape becomes a trimap: outside it is certainly background; a thin core well
 *     inside is taken as foreground to start with; the rest is unknown.
 *  2. Colour models — quantised RGB histograms, one for each side — are built from the current
 *     labelling. Histograms rather than the Gaussian mixtures of the original paper: no matrix
 *     inversions, no degenerate covariances on a flat region, and on a photograph the answers
 *     are within a hair of each other.
 *  3. A graph is built with one node per pixel: how well a pixel's colour fits each side becomes
 *     its pull towards that side, and every neighbouring pair is tied together by a bond that is
 *     weak where the picture already has an edge and strong where it is flat.
 *  4. The minimum cut of that graph is the boundary. Steps 2–4 repeat a few times, each pass
 *     re-learning the colours from a better labelling.
 *
 * Everything is integer arithmetic on typed arrays: no allocation inside the inner loops, and
 * no floating-point comparisons deciding the topology of a cut.
 */

// ── Tuning ────────────────────────────────────────────────────────────────────────────────
// GAMMA is how much the picture's own edges matter against its colours; 50 is the value from
// the original paper and behaves well here. COST_SCALE turns the log-likelihoods into integers
// so the max-flow never has to compare floats. ITERATIONS is where the answer stops improving
// on the pictures this was measured against — the fourth pass moved under a tenth of a percent.
const GAMMA = 50;
const COST_SCALE = 64;
const ITERATIONS = 3;
const INF = 0x3fffffff;

// 5 bits a channel: 32768 buckets. Fine enough to tell a face from a green wall, coarse enough
// that a few thousand pixels populate it densely instead of scattering one to a bucket.
const BITS = 5;
const LEVELS = 1 << BITS;                     // 32
const BINS = LEVELS * LEVELS * LEVELS;        // 32768
const SHIFT = 8 - BITS;                       // 3

const binOf = (r, g, b) =>
  ((r >> SHIFT) << (BITS * 2)) | ((g >> SHIFT) << BITS) | (b >> SHIFT);

/**
 * Boykov–Kolmogorov maximum flow on a sparse graph, specialised for grids.
 *
 * Written out rather than pulled in: the graph here is built once per pass and thrown away, so
 * what matters is that the arcs live in flat typed arrays with no per-edge objects. Arcs are
 * stored in pairs, so an arc's reverse is always `a ^ 1`.
 *
 * `trcap[n]` holds the node's terminal capacity: positive is residual capacity from the source,
 * negative is residual capacity to the sink. A node can never have both.
 */
export class MaxFlow {
  constructor(nodes, maxArcs) {
    this.n = nodes;
    this.first = new Int32Array(nodes).fill(-1);
    this.head = new Int32Array(maxArcs * 2);
    this.next = new Int32Array(maxArcs * 2);
    this.cap = new Int32Array(maxArcs * 2);
    this.arcs = 0;
    this.trcap = new Int32Array(nodes);
    // Search state
    this.parentArc = new Int32Array(nodes).fill(-1);   // the arc we were reached by
    this.tree = new Int8Array(nodes);                  // 0 free, 1 source tree, 2 sink tree
    this.ts = new Int32Array(nodes);                   // time stamp of the last validity check
    this.dist = new Int32Array(nodes);                 // distance to the tree's root
    // A ring, not a list that grows: a node re-enters the queue many times over a run, so a
    // plain append would walk off the end of the array on any picture big enough to matter.
    // `inActive` guarantees at most one copy of each node is waiting, so n+1 slots always fit.
    this.active = new Int32Array(nodes + 1);
    this.inActive = new Uint8Array(nodes);
    this.time = 0;
    this.throughFlow = 0;
  }

  /** An undirected bond of equal capacity both ways — what a neighbouring pair of pixels is. */
  addEdge(u, v, capUV, capVU) {
    const a = this.arcs * 2;
    this.head[a] = v; this.cap[a] = capUV; this.next[a] = this.first[u]; this.first[u] = a;
    this.head[a + 1] = u; this.cap[a + 1] = capVU; this.next[a + 1] = this.first[v]; this.first[v] = a + 1;
    this.arcs++;
  }

  /**
   * How strongly this node is pulled to the source (positive) or the sink (negative).
   *
   * A node pulled BOTH ways carries min(a, b) units of flow straight through, source → node →
   * sink, before any path-finding happens; only the difference can ever be contested. Folding
   * the two into one signed number is what makes the search cheap, but it silently drops that
   * through-flow, so it is banked here instead. The labelling is unaffected either way — this
   * is only the difference between reporting the true maximum flow and reporting part of it.
   */
  addTerminal(node, toSource, toSink) {
    this.throughFlow += Math.min(toSource, toSink);
    this.trcap[node] += toSource - toSink;
  }

  maxflow() {
    const { first, head, next, cap, trcap, parentArc, tree, dist, ts } = this;
    const active = this.active, inActive = this.inActive;
    const RING = this.n + 1;
    let aHead = 0, aTail = 0, aCount = 0;
    const orphans = [];
    let oHead = 0;

    const setActive = (n) => {
      if (inActive[n]) return;
      inActive[n] = 1; active[aTail] = n; aTail = (aTail + 1) % RING; aCount++;
    };
    const popActive = () => {
      const n = active[aHead]; aHead = (aHead + 1) % RING; aCount--; inActive[n] = 0; return n;
    };

    for (let n = 0; n < this.n; n++) {
      if (trcap[n] !== 0) {
        tree[n] = trcap[n] > 0 ? 1 : 2;
        parentArc[n] = -2;            // -2 marks a root: its parent is the terminal itself
        dist[n] = 1; ts[n] = 0;
        setActive(n);
      } else {
        tree[n] = 0; parentArc[n] = -1;
      }
    }

    // Is this node still genuinely connected to its tree's root? Walking up and stamping keeps
    // the check near-constant after the first traversal, which is the whole point of BK.
    const isRooted = (n) => {
      let stack = 0, path = this._path || (this._path = new Int32Array(this.n + 1));
      let cur = n;
      while (true) {
        if (ts[cur] === this.time) {                       // already verified this round
          for (let i = 0; i < stack; i++) { ts[path[i]] = this.time; dist[path[i]] = dist[cur] + (stack - i); }
          return true;
        }
        const pa = parentArc[cur];
        if (pa === -2) {                                   // a root
          ts[cur] = this.time; dist[cur] = 1;
          for (let i = 0; i < stack; i++) { ts[path[i]] = this.time; dist[path[i]] = 1 + (stack - i); }
          return true;
        }
        if (pa === -1) return false;                       // orphaned somewhere up the chain
        path[stack++] = cur;
        cur = head[pa];
      }
    };

    let flow = 0;
    while (true) {
      // ── Grow ────────────────────────────────────────────────────────────────────────────
      let p = -1, connecting = -1;
      while (aCount > 0) {
        const n = active[aHead];
        if (tree[n] === 0) { popActive(); continue; }
        let found = -1;
        for (let a = first[n]; a !== -1; a = next[a]) {
          // A source tree grows along arcs with residual capacity out of n; a sink tree grows
          // along arcs with residual capacity INTO n, which is the reverse arc.
          const arc = tree[n] === 1 ? a : (a ^ 1);
          if (cap[arc] <= 0) continue;
          const q = head[a];
          if (tree[q] === 0) {
            tree[q] = tree[n]; parentArc[q] = a ^ 1; ts[q] = ts[n]; dist[q] = dist[n] + 1;
            setActive(q);
          } else if (tree[q] !== tree[n]) { found = a; break; }
        }
        if (found !== -1) { p = n; connecting = found; break; }   // stays active: BK re-scans it
        popActive();
      }
      if (p === -1) break;                                  // no path left: the flow is maximum

      // ── Augment ─────────────────────────────────────────────────────────────────────────
      // The bottleneck along source-root → p → q → sink-root.
      const q = head[connecting];
      const srcSide = tree[p] === 1 ? p : q;
      const snkSide = tree[p] === 1 ? q : p;
      const middle = tree[p] === 1 ? connecting : (connecting ^ 1);   // always source→sink
      let bottleneck = cap[middle];
      for (let n = srcSide; ; ) {
        const pa = parentArc[n];
        if (pa < 0) break;
        const up = pa ^ 1;                                  // arc from the parent down to n
        if (cap[up] < bottleneck) bottleneck = cap[up];
        n = head[pa];
      }
      let rootS = srcSide; while (parentArc[rootS] >= 0) rootS = head[parentArc[rootS]];
      let rootT = snkSide; while (parentArc[rootT] >= 0) rootT = head[parentArc[rootT]];
      if (trcap[rootS] < bottleneck) bottleneck = trcap[rootS];
      if (-trcap[rootT] < bottleneck) bottleneck = -trcap[rootT];
      for (let n = snkSide; ; ) {
        const pa = parentArc[n];
        if (pa < 0) break;
        if (cap[pa] < bottleneck) bottleneck = cap[pa];
        n = head[pa];
      }

      cap[middle] -= bottleneck; cap[middle ^ 1] += bottleneck;
      for (let n = srcSide; ; ) {
        const pa = parentArc[n];
        if (pa < 0) break;
        const up = pa ^ 1;
        cap[up] -= bottleneck; cap[pa] += bottleneck;
        if (cap[up] === 0) { parentArc[n] = -1; orphans.push(n); }
        n = head[pa];
      }
      for (let n = snkSide; ; ) {
        const pa = parentArc[n];
        if (pa < 0) break;
        cap[pa] -= bottleneck; cap[pa ^ 1] += bottleneck;
        if (cap[pa] === 0) { parentArc[n] = -1; orphans.push(n); }
        n = head[pa];
      }
      trcap[rootS] -= bottleneck;
      trcap[rootT] += bottleneck;
      if (trcap[rootS] === 0) { parentArc[rootS] = -1; orphans.push(rootS); }
      if (trcap[rootT] === 0) { parentArc[rootT] = -1; orphans.push(rootT); }
      flow += bottleneck;

      // ── Adopt ───────────────────────────────────────────────────────────────────────────
      this.time++;
      while (oHead < orphans.length) {
        const o = orphans[oHead++];
        if (tree[o] === 0) continue;
        const mine = tree[o];
        let best = -1, bestDist = 0x7fffffff;
        for (let a = first[o]; a !== -1; a = next[a]) {
          const q2 = head[a];
          if (tree[q2] !== mine) continue;
          const arc = mine === 1 ? (a ^ 1) : a;             // capacity from q2 towards o
          if (cap[arc] <= 0) continue;
          if (!isRooted(q2)) continue;
          if (dist[q2] < bestDist) { bestDist = dist[q2]; best = a; }
        }
        if (best !== -1) {
          parentArc[o] = best; dist[o] = bestDist + 1; ts[o] = this.time;
          continue;
        }
        // Nobody can adopt it: it leaves the tree, and its neighbours may be orphaned too.
        for (let a = first[o]; a !== -1; a = next[a]) {
          const q2 = head[a];
          if (tree[q2] !== mine) continue;
          const arc = mine === 1 ? (a ^ 1) : a;
          if (cap[arc] > 0) setActive(q2);
          if (parentArc[q2] >= 0 && head[parentArc[q2]] === o) { parentArc[q2] = -1; orphans.push(q2); }
        }
        // `inActive` is deliberately left as it is: it means "waiting in the ring", not "in a
        // tree". Clearing it here would let the node be queued a second time while the first
        // copy is still waiting, and two copies of one node is how a ring sized for n overflows.
        // A freed node still in the ring is simply popped and skipped by the grow loop.
        tree[o] = 0; parentArc[o] = -1;
      }
      orphans.length = 0; oHead = 0;
    }
    return flow + this.throughFlow;
  }

  /** After maxflow: 1 where the node ended on the source side. */
  sourceSide() {
    const out = new Uint8Array(this.n);
    for (let i = 0; i < this.n; i++) out[i] = this.tree[i] === 1 ? 1 : 0;
    return out;
  }
}

/**
 * Segment a region using a rough drawn mask as the hint.
 *
 * @param {Uint8ClampedArray} rgba   pixels, w*h*4
 * @param {number} w
 * @param {number} h
 * @param {Uint8Array} hint          1 inside the drawn shape, 0 outside (length w*h)
 * @param {object} [opts]
 * @param {number} [opts.iterations] passes of re-learning the colours
 * @returns {Uint8Array|null}        1 for object, 0 for background — or null if the hint is
 *                                   too small or one side has no colours to learn from
 */
export function grabCut(rgba, w, h, hint, opts = {}) {
  const n = w * h;
  if (!n || hint.length !== n) return null;

  // ── The trimap ──────────────────────────────────────────────────────────────────────────
  // Outside the drawn shape is background and stays background: the person drew round the
  // thing, and honouring that is what makes the result predictable. Inside, only a core well
  // clear of the boundary is assumed to be object, because a shape drawn "roughly round" a head
  // certainly contains wall near its edge. The band between the two is what gets decided.
  let area = 0;
  for (let i = 0; i < n; i++) if (hint[i]) area++;
  if (area < 64 || area >= n) return null;

  // A core drawn in from the boundary by a fraction of the shape's own size: enough to be
  // inside the object on any sanely drawn lasso, never so much that nothing is left.
  // ── How far in the core sits ────────────────────────────────────────────────────────────
  // This is the one number that decides what the object's colours are learnt from, so it is
  // set by what a drawn shape is actually like rather than by what looks tidy. People draw an
  // oval round a head; an oval round a head contains wall at the top and in all four corners,
  // and a shallow erosion still reaches it. At a quarter of the shape's own size the core is
  // the middle of the face on any lasso worth calling one, and the wall is left for the cut to
  // decide about — which, with the core no longer pinned as certain, it can.
  const side = Math.sqrt(area);
  const erode = Math.max(2, Math.round(side * 0.26));
  const core = erodeMask(hint, w, h, erode);
  let coreArea = 0;
  for (let i = 0; i < n; i++) if (core[i]) coreArea++;
  if (coreArea < 32) {                      // a thin lasso: fall back to a single-pass erosion
    const light = erodeMask(hint, w, h, 1);
    core.set(light);
    coreArea = 0; for (let i = 0; i < n; i++) if (core[i]) coreArea++;
    if (coreArea < 16) return null;
  }

  // ── How much the picture's own edges matter ─────────────────────────────────────────────
  // beta normalises the colour differences so that an "average" neighbouring pair costs about
  // the same on any picture — a flat studio shot and a noisy phone photo both behave.
  let sum = 0, pairs = 0;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = y * w + x;
      if (x + 1 < w) { sum += diff2(rgba, i, i + 1); pairs++; }
      if (y + 1 < h) { sum += diff2(rgba, i, i + w); pairs++; }
      if (x + 1 < w && y + 1 < h) { sum += diff2(rgba, i, i + w + 1); pairs++; }
      if (x > 0 && y + 1 < h) { sum += diff2(rgba, i, i + w - 1); pairs++; }
    }
  }
  const mean = pairs ? sum / pairs : 1;
  const beta = mean > 0 ? 1 / (2 * mean) : 0;
  const DIAG = Math.SQRT1_2;

  // ── What the first colour model is learnt from ──────────────────────────────────────────
  // The core, not the whole drawn shape. Drawing "roughly round" a head means the shape's
  // corners are wall, so learning the object's colours from everything inside it teaches the
  // object model that wall is part of the object — and the first cut then keeps the wall,
  // which the passes after it only reinforce. The core is the part nobody would draw round
  // unless they meant it. Everything between the core and the outline is left unlabelled and
  // is what the cut is actually for.
  const labels = new Uint8Array(n);
  labels.set(core);

  const fgHist = new Float64Array(BINS);
  const bgHist = new Float64Array(BINS);
  const fgCost = new Int32Array(BINS);
  const bgCost = new Int32Array(BINS);

  const iterations = opts.iterations || ITERATIONS;
  let out = labels;

  for (let pass = 0; pass < iterations; pass++) {
    // ── Learn the two colour models from the labelling we have ────────────────────────────
    fgHist.fill(0); bgHist.fill(0);
    let fgN = 0, bgN = 0;
    for (let i = 0; i < n; i++) {
      const p = i * 4;
      const b = binOf(rgba[p], rgba[p + 1], rgba[p + 2]);
      // The object is learnt from what is currently labelled object; the background from
      // everything outside the drawn shape, which is the part we are certain about. Pixels
      // inside the shape that the last pass called background are NOT used as background
      // samples — early on those are simply the parts of the object the model had not yet
      // learnt, and feeding them back is how a segmentation eats its own subject.
      if (labels[i]) { fgHist[b]++; fgN++; }
      // ── What counts as a background sample ──────────────────────────────────────────
      // On the FIRST pass, only what lies outside the drawn shape: the labelling inside it is
      // just the eroded core and a guess, and treating that guess as training data would bake
      // it in before anything has been measured.
      //
      // Afterwards, everything the cut called background counts — including the parts inside
      // the drawn shape. That is the point of iterating. A strip of green doorframe that sits
      // inside the lasso beside the head is also present just outside it; once the first cut
      // has correctly dropped the outside part, the model learns that green is background and
      // the next cut drops the strip beside the head too. Restricting the samples to the thin
      // ring outside the shape left that strip attached to the head however many passes ran,
      // because nothing new was ever learnt.
      else if (!hint[i] || pass > 0) { bgHist[b]++; bgN++; }
    }
    if (fgN < 16 || bgN < 16) return null;

    // -log of the probability, as an integer. The prior stops an unseen colour costing
    // infinity, which would let one stray pixel decide a boundary.
    const fgPrior = 1 / (fgN + BINS), bgPrior = 1 / (bgN + BINS);
    for (let b = 0; b < BINS; b++) {
      fgCost[b] = Math.round(-Math.log((fgHist[b] + 1) * fgPrior) * COST_SCALE);
      bgCost[b] = Math.round(-Math.log((bgHist[b] + 1) * bgPrior) * COST_SCALE);
    }

    // ── Build the graph ───────────────────────────────────────────────────────────────────
    const maxArcs = 4 * n;
    const g = new MaxFlow(n, maxArcs);
    for (let i = 0; i < n; i++) {
      if (!hint[i]) {
        g.addTerminal(i, 0, INF);            // certainly background
        continue;
      }
      // The core is a hint, never a verdict. Nailing it down as certain foreground put a
      // bright wedge of doorframe permanently inside the head: the drawn shape was an oval
      // round a head, so its centre band reached wall at the top, and once that wall was
      // pinned as object every later pass defended it. The core still seeds the first colour
      // model, which is all it is good for — where the boundary actually goes is the cut's
      // business, and the cut can now move it.
      const p = i * 4;
      const b = binOf(rgba[p], rgba[p + 1], rgba[p + 2]);
      // Source capacity is what it costs to call this pixel background, and vice versa.
      g.addTerminal(i, bgCost[b], fgCost[b]);
    }
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const i = y * w + x;
        if (x + 1 < w) { const c = bond(rgba, i, i + 1, beta, 1); g.addEdge(i, i + 1, c, c); }
        if (y + 1 < h) { const c = bond(rgba, i, i + w, beta, 1); g.addEdge(i, i + w, c, c); }
        if (x + 1 < w && y + 1 < h) { const c = bond(rgba, i, i + w + 1, beta, DIAG); g.addEdge(i, i + w + 1, c, c); }
        if (x > 0 && y + 1 < h) { const c = bond(rgba, i, i + w - 1, beta, DIAG); g.addEdge(i, i + w - 1, c, c); }
      }
    }

    g.maxflow();
    const next = g.sourceSide();
    for (let i = 0; i < n; i++) if (!hint[i]) next[i] = 0;   // never escape the drawn shape

    let changed = 0, kept = 0;
    for (let i = 0; i < n; i++) { if (next[i] !== labels[i]) changed++; if (next[i]) kept++; }
    if (kept < 32) return out === labels ? null : out;       // it ate the subject; keep the last good one
    labels.set(next);
    out = labels;
    if (changed < n * 0.001) break;                          // settled
  }
  return out;
}

/**
 * The studio's entry point: segment inside a drawn mask, at whatever size is affordable.
 *
 * Two things stand between `grabCut` and the studio. The sample image is up to 800 pixels
 * wide and can be over a million pixels, where a minimum cut costs seconds rather than a
 * fraction of one; and almost all of that is nowhere near the drawn shape, so it contributes
 * nothing but work. This crops to the shape with a margin of background round it — the
 * background is not scenery here, it is the training data for one of the two colour models —
 * shrinks that to a size worth cutting, and lifts the answer back.
 *
 * @param {{data: Uint8ClampedArray, width: number, height: number}} imgData
 * @param {Uint8Array} mask  the drawn shape, 1 inside, length width*height
 * @param {object} [opts]
 * @param {number} [opts.maxPixels]  working size; ~160k is about a third of a second
 * @returns {Uint8Array|null} a mask in the ORIGINAL size, or null if it could not be done
 */
export function grabCutInMask(imgData, mask, opts = {}) {
  const W = imgData.width, H = imgData.height, src = imgData.data;
  const maxPixels = opts.maxPixels || 160000;
  let minX = W, minY = H, maxX = -1, maxY = -1, area = 0;
  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W; x++) {
      if (!mask[y * W + x]) continue;
      area++;
      if (x < minX) minX = x; if (x > maxX) maxX = x;
      if (y < minY) minY = y; if (y > maxY) maxY = y;
    }
  }
  if (maxX < minX || area < 64) return null;

  // Room round the shape, so there is background to learn from. A quarter of the shape's size
  // on each side: enough wall beside a head, never so much that the crop is mostly scenery.
  const bw0 = maxX - minX + 1, bh0 = maxY - minY + 1;
  const pad = Math.max(12, Math.round(Math.max(bw0, bh0) * 0.25));
  const x0 = Math.max(0, minX - pad), y0 = Math.max(0, minY - pad);
  const x1 = Math.min(W - 1, maxX + pad), y1 = Math.min(H - 1, maxY + pad);
  const bw = x1 - x0 + 1, bh = y1 - y0 + 1;
  if (bw < 8 || bh < 8) return null;

  const scale = Math.min(1, Math.sqrt(maxPixels / (bw * bh)));
  const w = Math.max(8, Math.round(bw * scale)), h = Math.max(8, Math.round(bh * scale));

  // Box-average down, so a shrunk photograph keeps its colours instead of picking up whichever
  // pixel a nearest-neighbour sample happened to land on — the colour models are the whole of
  // this method, and sampling noise into them is sampling noise into the answer.
  const small = new Uint8ClampedArray(w * h * 4);
  const smallMask = new Uint8Array(w * h);
  for (let yy = 0; yy < h; yy++) {
    const sy0 = y0 + Math.floor((yy * bh) / h), sy1 = y0 + Math.max(Math.floor(((yy + 1) * bh) / h), Math.floor((yy * bh) / h) + 1);
    for (let xx = 0; xx < w; xx++) {
      const sx0 = x0 + Math.floor((xx * bw) / w), sx1 = x0 + Math.max(Math.floor(((xx + 1) * bw) / w), Math.floor((xx * bw) / w) + 1);
      let r = 0, g = 0, b = 0, n = 0, on = 0;
      for (let sy = sy0; sy < sy1 && sy <= y1; sy++) {
        for (let sx = sx0; sx < sx1 && sx <= x1; sx++) {
          const p = (sy * W + sx) * 4;
          r += src[p]; g += src[p + 1]; b += src[p + 2]; n++;
          if (mask[sy * W + sx]) on++;
        }
      }
      const d = (yy * w + xx) * 4;
      if (n) { small[d] = r / n; small[d + 1] = g / n; small[d + 2] = b / n; }
      small[d + 3] = 255;
      smallMask[yy * w + xx] = n && on * 2 >= n ? 1 : 0;   // majority
    }
  }

  let lab = grabCut(small, w, h, smallMask, opts);
  if (!lab) return null;
  lab = fillHoles(largestIsland(lab, w, h), w, h);

  // Lift back. Bilinear on the labels rather than nearest, so a boundary that was decided three
  // pixels at a time does not come back as a staircase.
  const out = new Uint8Array(W * H);
  for (let y = y0; y <= y1; y++) {
    const fy = ((y - y0 + 0.5) * h) / bh - 0.5;
    const ya = Math.max(0, Math.min(h - 1, Math.floor(fy))), yb = Math.max(0, Math.min(h - 1, ya + 1));
    const wy = Math.max(0, Math.min(1, fy - ya));
    for (let x = x0; x <= x1; x++) {
      if (!mask[y * W + x]) continue;                 // never outside what was drawn
      const fx = ((x - x0 + 0.5) * w) / bw - 0.5;
      const xa = Math.max(0, Math.min(w - 1, Math.floor(fx))), xb = Math.max(0, Math.min(w - 1, xa + 1));
      const wx = Math.max(0, Math.min(1, fx - xa));
      const v = lab[ya * w + xa] * (1 - wx) * (1 - wy) + lab[ya * w + xb] * wx * (1 - wy)
        + lab[yb * w + xa] * (1 - wx) * wy + lab[yb * w + xb] * wx * wy;
      if (v >= 0.5) out[y * W + x] = 1;
    }
  }
  let kept = 0;
  for (let i = 0; i < W * H; i++) if (out[i]) kept++;
  // Nothing worth having, or it simply handed the drawn shape straight back: say so rather
  // than dressing the drawn shape up as a detection.
  if (kept < 64 || kept > area * 0.985) return null;
  return out;
}

/** Squared colour distance between two pixels. */
function diff2(rgba, i, j) {
  const a = i * 4, b = j * 4;
  const dr = rgba[a] - rgba[b], dg = rgba[a + 1] - rgba[b + 1], db = rgba[a + 2] - rgba[b + 2];
  return dr * dr + dg * dg + db * db;
}

/** How strongly two neighbouring pixels are tied: weak across an edge, strong across flat. */
function bond(rgba, i, j, beta, scale) {
  const d = diff2(rgba, i, j);
  return Math.max(1, Math.round(GAMMA * scale * Math.exp(-beta * d) * COST_SCALE));
}

/**
 * Shrink a mask by `r` pixels. Two chamfer sweeps give the distance to the nearest zero, which
 * is both faster and rounder than r passes of a 3×3 erosion.
 */
export function erodeMask(mask, w, h, r) {
  const n = w * h;
  const BIG = 1 << 28;
  const d = new Int32Array(n);
  for (let i = 0; i < n; i++) d[i] = mask[i] ? BIG : 0;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = y * w + x;
      if (!d[i]) continue;
      let m = d[i];
      if (x > 0 && d[i - 1] + 3 < m) m = d[i - 1] + 3;
      if (y > 0 && d[i - w] + 3 < m) m = d[i - w] + 3;
      if (x > 0 && y > 0 && d[i - w - 1] + 4 < m) m = d[i - w - 1] + 4;
      if (x + 1 < w && y > 0 && d[i - w + 1] + 4 < m) m = d[i - w + 1] + 4;
      d[i] = m;
    }
  }
  for (let y = h - 1; y >= 0; y--) {
    for (let x = w - 1; x >= 0; x--) {
      const i = y * w + x;
      if (!d[i]) continue;
      let m = d[i];
      if (x + 1 < w && d[i + 1] + 3 < m) m = d[i + 1] + 3;
      if (y + 1 < h && d[i + w] + 3 < m) m = d[i + w] + 3;
      if (x + 1 < w && y + 1 < h && d[i + w + 1] + 4 < m) m = d[i + w + 1] + 4;
      if (x > 0 && y + 1 < h && d[i + w - 1] + 4 < m) m = d[i + w - 1] + 4;
      d[i] = m;
    }
  }
  const cut = r * 3;
  const out = new Uint8Array(n);
  for (let i = 0; i < n; i++) out[i] = d[i] > cut ? 1 : 0;
  return out;
}

/** The largest connected run of 1s, with everything else dropped. */
export function largestIsland(mask, w, h) {
  const n = w * h;
  const label = new Int32Array(n);
  const queue = new Int32Array(n);
  let next = 1, best = 0, bestN = 0;
  for (let i = 0; i < n; i++) {
    if (!mask[i] || label[i]) continue;
    const id = next++;
    let head = 0, tail = 0, count = 0;
    queue[tail++] = i; label[i] = id;
    while (head < tail) {
      const q = queue[head++]; count++;
      const x = q % w, y = (q - x) / w;
      if (x > 0 && mask[q - 1] && !label[q - 1]) { label[q - 1] = id; queue[tail++] = q - 1; }
      if (x + 1 < w && mask[q + 1] && !label[q + 1]) { label[q + 1] = id; queue[tail++] = q + 1; }
      if (y > 0 && mask[q - w] && !label[q - w]) { label[q - w] = id; queue[tail++] = q - w; }
      if (y + 1 < h && mask[q + w] && !label[q + w]) { label[q + w] = id; queue[tail++] = q + w; }
    }
    if (count > bestN) { bestN = count; best = id; }
  }
  if (!best) return mask;
  const out = new Uint8Array(n);
  for (let i = 0; i < n; i++) if (label[i] === best) out[i] = 1;
  return out;
}

/** Fill holes that do not touch the mask's own border — an eye or a nostril read as background. */
export function fillHoles(mask, w, h) {
  const n = w * h;
  const outside = new Uint8Array(n);
  const queue = new Int32Array(n);
  let tail = 0;
  for (let x = 0; x < w; x++) {
    if (!mask[x]) { outside[x] = 1; queue[tail++] = x; }
    const b = (h - 1) * w + x;
    if (!mask[b] && !outside[b]) { outside[b] = 1; queue[tail++] = b; }
  }
  for (let y = 0; y < h; y++) {
    const l = y * w, r = y * w + w - 1;
    if (!mask[l] && !outside[l]) { outside[l] = 1; queue[tail++] = l; }
    if (!mask[r] && !outside[r]) { outside[r] = 1; queue[tail++] = r; }
  }
  let head = 0;
  while (head < tail) {
    const q = queue[head++];
    const x = q % w, y = (q - x) / w;
    if (x > 0 && !mask[q - 1] && !outside[q - 1]) { outside[q - 1] = 1; queue[tail++] = q - 1; }
    if (x + 1 < w && !mask[q + 1] && !outside[q + 1]) { outside[q + 1] = 1; queue[tail++] = q + 1; }
    if (y > 0 && !mask[q - w] && !outside[q - w]) { outside[q - w] = 1; queue[tail++] = q - w; }
    if (y + 1 < h && !mask[q + w] && !outside[q + w]) { outside[q + w] = 1; queue[tail++] = q + w; }
  }
  const out = new Uint8Array(n);
  for (let i = 0; i < n; i++) out[i] = mask[i] || !outside[i] ? 1 : 0;
  return out;
}
